<template>
    <textarea ref="inputRef" :id="_id" :name="name" v-model="input" :required="_required" v-bind="Object.assign({}, $attrs, ariaValidationAttributes)"></textarea>
</template>

<script setup lang="ts">
    import {useAbstractFormInput} from '@/composables/abstractFormInput';
    import {usePortuna} from '@/composables/portuna';
    import {FormRowKey} from '@/library/form';
    import {ref, nextTick, watch, toRefs, inject} from 'vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'update:modelValue', modelValue: Optional<InputValueType>): void;
    }>();

    const props = withDefaults(
        defineProps<{
            id?: string;
            name: string;
            required?: boolean;
            overwriteRequired?: boolean;
            errorName?: string;
            filterName?: string;
            modelValue?: string;
        }>(),
        {
            required: false,
            overwriteRequired: false,
            errorName: '',
            filterName: '',
            modelValue: '',
        },
    );

    const parentFormRow = inject<Nullable<IFormRowContext>>(FormRowKey, null);

    const inputRef = ref<HTMLTextAreaElement>();

    const {
              id,
              name,
              required,
              overwriteRequired,
              errorName,
              filterName,
              modelValue,
          } = toRefs(props);

    const {
              _id,
              _required,
              ariaValidationAttributes,
              validateValue,
              expose,
          } = useAbstractFormInput(
        parentFormRow,
        id,
        required,
        overwriteRequired,
        errorName,
        filterName,
    );

    const timeout = ref<number>(0);
    const input   = ref<string>(modelValue.value);

    const updateValue = () =>
    {
        let inputProxy = input.value;

        if (inputRef.value)
        {
            inputProxy = inputRef.value.value;
        }

        return validateValue(
            inputProxy,
        );
    };

    watch(input, (newValue: string) =>
    {
        emit('update:modelValue', newValue);
    });

    watch(modelValue, (newValue: string, oldValue: string) =>
    {
        // 'fake' debounce
        if (timeout.value)
        {
            clearTimeout(timeout.value);
        }

        timeout.value = window.setTimeout(() =>
        {
            input.value = newValue;

            if (newValue !== oldValue)
            {
                nextTick(updateValue);
            }
        }, 200);
    });

    defineExpose(Object.assign({}, portuna, expose, {
        componentName: 'FormTextarea',
    }));
</script>